import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, ACCENT_HEX } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Trash2, Plus, PhoneCall, ShieldAlert, Zap, HeartHandshake, XCircle } from "lucide-react";

const ACCENTS = ["blue", "amber", "green", "purple", "red"];
const EMPTY = { state: "", en_title: "", hi_title: "", information_en: "", information_hi: "", what_happened_en: "", what_happened_hi: "", how_it_happened_en: "", how_it_happened_hi: "", avoided_en: "", avoided_hi: "", ideal_response_en: "", ideal_response_hi: "", accent: "blue" };

export default function CaseStudies() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [selectedCase, setSelectedCase] = useState(null);

  const load = () => api.get("/case-studies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const add = async () => {
    setSaving(true);
    try {
      await api.post("/admin/case-studies", form);
      toast.success("Case study added");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/case-studies/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={t("caseStudies")} subtitle="Anonymized detailed case studies by state"
        action={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F172A] hover:bg-slate-800" data-testid="add-case-study"><Plus className="h-4 w-4 mr-1.5" />Add Case Study</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add Case Study</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>State</Label><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="cs-state" className="mt-1" /></div>
                  <div><Label>Accent</Label>
                    <select value={form.accent} onChange={(e) => set("accent", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="cs-accent">
                      {ACCENTS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Title (EN)</Label><Input value={form.en_title} onChange={(e) => set("en_title", e.target.value)} className="mt-1" /></div>
                  <div><Label>Title (HI)</Label><Input value={form.hi_title} onChange={(e) => set("hi_title", e.target.value)} className="mt-1" /></div>
                </div>
                <div><Label>Call Context (EN)</Label><Textarea value={form.call_context_en} onChange={(e) => set("call_context_en", e.target.value)} className="mt-1" rows={2} /></div>
                <div><Label>Call Context (HI)</Label><Textarea value={form.call_context_hi} onChange={(e) => set("call_context_hi", e.target.value)} className="mt-1" rows={2} /></div>
              </div>
              <DialogFooter>
                <Button onClick={add} disabled={saving || !form.state} className="bg-[#0F172A] hover:bg-slate-800" data-testid="cs-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((c) => {
          const hex = ACCENT_HEX[c.accent] || "#2563EB";
          return (
            <Card key={c.id || c.state} className="border-l-4 relative cursor-pointer hover:shadow-lg transition-shadow" style={{ borderLeftColor: hex }} data-testid={`case-${c.state}`} onClick={() => setSelectedCase(c)}>
              {isAdmin && c.id && (
                <button onClick={(e) => { e.stopPropagation(); del(c.id); }} data-testid={`delete-case-${c.state}`}
                  className="absolute top-3 right-3 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
              )}
              <div className="flex items-center gap-2 mb-3 pr-6">
                <MapPin className="h-4 w-4" style={{ color: hex }} />
                <span className="font-head font-bold text-slate-900">{c.state}</span>
                <span className="text-xs rounded-full px-2 py-0.5 font-medium" style={{ backgroundColor: `${hex}15`, color: hex }}>
                  {lang === "en" ? c.en_title : c.hi_title}
                </span>
              </div>
              <div className="mb-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase mb-1">
                  <PhoneCall className="h-3.5 w-3.5" /> Call Context
                </div>
                <p className="text-[13px] text-slate-700 leading-relaxed line-clamp-3">{lang === "en" ? c.call_context_en : c.call_context_hi}</p>
              </div>
              <div className="mt-auto pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase mb-1">
                  <Zap className="h-3.5 w-3.5" /> Immediate Action
                </div>
                <p className="text-[13px] font-medium text-slate-900 line-clamp-2">{lang === "en" ? c.immediate_action_en : c.immediate_action_hi}</p>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!selectedCase} onOpenChange={(open) => !open && setSelectedCase(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0">
          {selectedCase && (() => {
            const hex = ACCENT_HEX[selectedCase.accent] || "#2563EB";
            return (
              <>
                <div className="p-6 text-white relative" style={{ backgroundColor: hex }}>
                  <div className="flex items-center gap-2 text-white/80 mb-2 text-sm font-semibold uppercase tracking-wider">
                    <MapPin className="h-4 w-4" /> {selectedCase.state} Report
                  </div>
                  <DialogTitle className="text-2xl font-bold text-white">
                    {lang === "en" ? selectedCase.en_title : selectedCase.hi_title}
                  </DialogTitle>
                </div>
                <div className="p-6 space-y-5 bg-slate-50 overflow-y-auto max-h-[70vh]">
                  
                  <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                      <PhoneCall className="h-4 w-4 text-blue-500" /> Initial Call Context
                    </div>
                    <p className="text-[15px] text-slate-800 leading-relaxed">
                      {lang === "en" ? selectedCase.call_context_en : selectedCase.call_context_hi}
                    </p>
                  </div>

                  <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                      <ShieldAlert className="h-4 w-4 text-red-500" /> Threat Assessment
                    </div>
                    <p className="text-[15px] text-slate-800 leading-relaxed">
                      {lang === "en" ? selectedCase.threat_assessment_en : selectedCase.threat_assessment_hi}
                    </p>
                  </div>

                  <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                      <Zap className="h-4 w-4 text-orange-500" /> Immediate Action (Golden Hour)
                    </div>
                    <p className="text-[15px] text-slate-800 leading-relaxed">
                      {lang === "en" ? selectedCase.immediate_action_en : selectedCase.immediate_action_hi}
                    </p>
                  </div>

                  <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                      <HeartHandshake className="h-4 w-4 text-amber-500" /> Victim Counseling
                    </div>
                    <p className="text-[15px] text-slate-800 leading-relaxed">
                      {lang === "en" ? selectedCase.counseling_en : selectedCase.counseling_hi}
                    </p>
                  </div>
                  
                  <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                      <XCircle className="h-4 w-4 text-emerald-500" /> Common Mistakes
                    </div>
                    <div className="p-4 rounded bg-emerald-50 border border-emerald-100 text-emerald-900 font-medium text-[15px] leading-relaxed">
                      {lang === "en" ? selectedCase.common_mistakes_en : selectedCase.common_mistakes_hi}
                    </div>
                  </div>

                </div>
                <div className="p-4 bg-white border-t border-slate-100 flex justify-end">
                  <Button variant="outline" onClick={() => setSelectedCase(null)}>Close Report</Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
